// 路由根：平台管理后台（Allinks）—— PRD 6 大中心 P0/P1 补齐
// 路由前缀统一为 /platform/*；P2 菜单结构性合并（65→41）：17 个 Tab 壳承接被合并叶，
// 壳内部 ?tab= 持态；全部旧路径重定向收敛于 routes/legacyRedirects.tsx
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ToastProvider } from '@/components/Toast';
import Layout from '@/components/Layout';
import ErrorBoundary from '@/components/ErrorBoundary';
import OfflineBanner from '@/components/OfflineBanner';
import { setAuthFailureHandler } from '@/api/client';
import RequireAuth from '@/auth/RequireAuth';
import RequireSetup from '@/auth/RequireSetup';
import LoginPage from '@/pages/auth';
import { legacyRedirectRoutes } from '@/routes/legacyRedirects';

// ===== 总览 =====
const OverviewPage = lazy(() => import('@/pages/overview/Overview'));
const NotificationsPage = lazy(() => import('@/pages/overview/Notifications'));

// ===== 账户中心 — 审核工作台（ACC-001~005，拆分自 AuditWorkbench）=====
const AgentOnboardingAuditPage = lazy(() => import('@/pages/account/audit/AgentOnboardingAudit'));
const MerchantOnboardingAuditPage = lazy(() => import('@/pages/account/audit/MerchantOnboardingAudit'));

// ===== 账户中心 — 代理商管理（ACC-006~009）=====
const AgentsPage = lazy(() => import('@/pages/account/Agents'));
const AgentDetailPage = lazy(() => import('@/pages/account/AgentDetail'));
const AgentTierPricePage = lazy(() => import('@/pages/account/AgentTierPrice'));
const AgentSpreadMonitorPage = lazy(() => import('@/pages/account/AgentSpreadMonitor'));
const AgentStatusPage = lazy(() => import('@/pages/account/AgentStatus'));
const CreateAgentPage = lazy(() => import('@/pages/account/CreateAgentPage'));

// ===== 账户中心 — 商户管理（ACC-010~014）=====
const MerchantsPage = lazy(() => import('@/pages/account/Merchants'));
const MerchantDetailPage = lazy(() => import('@/pages/account/MerchantDetail'));
const MerchantRatesPage = lazy(() => import('@/pages/account/MerchantRates'));
const MerchantCardsPage = lazy(() => import('@/pages/account/MerchantCards'));
const MerchantAgentLinkPage = lazy(() => import('@/pages/account/MerchantAgentLink'));
const MerchantStatusPage = lazy(() => import('@/pages/account/MerchantStatus'));
const CreateMerchantPage = lazy(() => import('@/pages/account/CreateMerchantPage'));

// ===== 账户中心 — 基础数据 / 上游渠道工作台（PAGE-PLT-ACC-016）=====
// 2026-09-19-N2 S7c：产品详情（费率设置）+ 产品费项目录（自财务中心挪入产品管理分区）
const UpstreamChannelsPage = lazy(() => import('@/pages/channel/UpstreamChannels'));
const ProductsPage = lazy(() => import('@/pages/product/Products'));
const ProductDetailPage = lazy(() => import('@/pages/product/ProductDetail'));
const ProductFeeCatalogPage = lazy(() => import('@/pages/product/ProductFeeCatalog'));
// 费率中心（费率中心 Sprint：链 A/链 B 试算 + 版本链追溯）
const RateCenterPage = lazy(() => import('@/pages/account/RateCenter'));

// ===== 卡片中心（CARD-001~009）=====
const CardOpsPage = lazy(() => import('@/pages/card/CardOps'));
const CardByMerchantPage = lazy(() => import('@/pages/card/CardByMerchant'));

// ===== 财务中心（FIN-001~010）=====
const ForceRefundsPage = lazy(() => import('@/pages/finance/ForceRefunds'));
const TreasuryPositionsPage = lazy(() => import('@/pages/finance/TreasuryPositions'));
const DepositReviewPage = lazy(() => import('@/pages/finance/DepositReview'));
const WithdrawalReviewPage = lazy(() => import('@/pages/finance/WithdrawalReview'));
const MerchantCreditReviewPage = lazy(() => import('@/pages/finance/MerchantCreditReview'));
const PositionTopupReviewPage = lazy(() => import('@/pages/finance/PositionTopupReview'));
const MerchantWalletPage = lazy(() => import('@/pages/finance/MerchantWallet'));
const VaAccountsPage = lazy(() => import('@/pages/finance/VaAccounts'));
const ChainAccountsPage = lazy(() => import('@/pages/finance/ChainAccounts'));

// ===== 风控合规 =====
const CompliancePage = lazy(() => import('@/pages/risk/Compliance'));

// ===== 运营中心（OPS-004b/005/008/009/010）=====
const AiProvidersPage = lazy(() => import('@/pages/ops/AiProvidersPage'));

// ===== 系统 =====
const EmailServicePage = lazy(() => import('@/pages/system/EmailService'));
const SecurityPage = lazy(() => import('@/pages/system/Security'));
const SettingsPage = lazy(() => import('@/pages/system/Settings'));

// ===== Tab 壳（P2 菜单合并 65→41；纯壳 ?tab= 持态 + lazy 挂载既有页面）=====
const AuditChangeBatchShell = lazy(() => import('@/pages/shell/AuditChangeBatch'));
const CardOverviewShell = lazy(() => import('@/pages/shell/CardOverview'));
const CardTransactionsShell = lazy(() => import('@/pages/shell/CardTransactions'));
const CardPhysicalShell = lazy(() => import('@/pages/shell/CardPhysical'));
const FinanceOverviewShell = lazy(() => import('@/pages/shell/FinanceOverview'));
const FinanceSettlementShell = lazy(() => import('@/pages/shell/FinanceSettlement'));
const FinanceSettlementBatchesShell = lazy(() => import('@/pages/shell/FinanceSettlementBatches'));
const RiskAlertsShell = lazy(() => import('@/pages/shell/RiskAlerts'));
const RiskRulesShell = lazy(() => import('@/pages/shell/RiskRules'));
const RiskKycReviewShell = lazy(() => import('@/pages/shell/RiskKycReview'));
const RiskAmlShell = lazy(() => import('@/pages/shell/RiskAml'));
const OpsContentShell = lazy(() => import('@/pages/shell/OpsContent'));
const OpsServiceShell = lazy(() => import('@/pages/shell/OpsService'));
const OpsSreShell = lazy(() => import('@/pages/shell/OpsSre'));
const ReportDashboardShell = lazy(() => import('@/pages/shell/ReportDashboard'));
const ReportSubscriptionsShell = lazy(() => import('@/pages/shell/ReportSubscriptions'));
const ReportCollabShell = lazy(() => import('@/pages/shell/ReportCollab'));

function PageFallback() {
  return <div className="p-10 text-center text-sm text-slate-400">加载页面…</div>;
}

/** 路由内部：注册 401 鉴权失败回调 → navigate('/login') */
function AppRoutes() {
  const navigate = useNavigate();
  useEffect(() => {
    setAuthFailureHandler(() => navigate('/login'));
    return () => setAuthFailureHandler(null);
  }, [navigate]);

  return (
    <Routes>
      {/* 公开路由：登录页 */}
      <Route path="/login" element={<LoginPage />} />

      {/* 受保护路由（RequireAuth 守卫：无 accessToken → /login；RequireSetup：未绑 2FA → 安全中心） */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireSetup />}>
        <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/platform" replace />} />

              {/* ===== 总览 ===== */}
              <Route path="platform" element={<OverviewPage />} />
              <Route path="platform/notifications" element={<NotificationsPage />} />

              {/* ===== 账户中心 — 审核工作台（ACC-001~005；变更+批量已合并入壳）===== */}
              <Route path="platform/account/audit/agent-onboarding" element={<AgentOnboardingAuditPage />} />
              <Route path="platform/account/audit/merchant-onboarding" element={<MerchantOnboardingAuditPage />} />
              <Route path="platform/account/audit/change-batch" element={<AuditChangeBatchShell />} />

              {/* ===== 账户中心 — 代理商管理（ACC-006~009）===== */}
              <Route path="platform/account/agents" element={<AgentsPage />} />
              <Route path="platform/account/agents/create" element={<CreateAgentPage />} />
              {/* 更具体子路由先于 :agentId 详情，避免误匹配 */}
              <Route path="platform/account/agents/:agentId/tier-price" element={<AgentTierPricePage />} />
              <Route path="platform/account/agents/:agentId/spread-monitor" element={<AgentSpreadMonitorPage />} />
              <Route path="platform/account/agents/:agentId/status" element={<AgentStatusPage />} />
              <Route path="platform/account/agents/:agentId" element={<AgentDetailPage />} />

              {/* ===== 账户中心 — 商户管理（ACC-010~014）===== */}
              <Route path="platform/account/merchants" element={<MerchantsPage />} />
              <Route path="platform/account/merchants/create" element={<CreateMerchantPage />} />
              {/* 更具体子路由先于 :merchantId 子路径，避免误匹配 */}
              <Route path="platform/account/merchants/:merchantId/rates" element={<MerchantRatesPage />} />
              <Route path="platform/account/merchants/:merchantId/cards" element={<MerchantCardsPage />} />
              <Route path="platform/account/merchants/:merchantId/agent-link" element={<MerchantAgentLinkPage />} />
              <Route path="platform/account/merchants/:merchantId/status" element={<MerchantStatusPage />} />
              <Route path="platform/account/merchants/:merchantId" element={<MerchantDetailPage />} />

              {/* ===== 账户中心 — 上游渠道工作台（PAGE-PLT-ACC-016）===== */}
              <Route path="platform/account/upstream-channels" element={<UpstreamChannelsPage />} />
              <Route path="platform/account/products" element={<ProductsPage />} />
              {/* 产品详情 · 费率设置（PAGE-PLT-ACC-018）；产品费项目录（自财务中心挪入） */}
              <Route path="platform/account/products/fee-items" element={<ProductFeeCatalogPage />} />
              <Route path="platform/account/products/:productId" element={<ProductDetailPage />} />
              {/* 费率中心（费率中心 Sprint：试算 + 版本链历史 + 回滚） */}
              <Route path="platform/account/rate-center" element={<RateCenterPage />} />

              {/* ===== 卡片中心（CARD-001~009；总览/交易/实体卡已合并入壳）===== */}
              <Route path="platform/card/overview" element={<CardOverviewShell />} />
              <Route path="platform/card/ops" element={<CardOpsPage />} />
              <Route path="platform/card/by-merchant" element={<CardByMerchantPage />} />
              <Route path="platform/card/transactions" element={<CardTransactionsShell />} />
              <Route path="platform/card/physical" element={<CardPhysicalShell />} />

              {/* ===== 财务中心（FIN-001~010；总览/结算对账/批次对账已合并入壳）===== */}
              <Route path="platform/finance/overview" element={<FinanceOverviewShell />} />
              <Route path="platform/finance/settlement" element={<FinanceSettlementShell />} />
              <Route path="platform/finance/settlement-batches" element={<FinanceSettlementBatchesShell />} />
              <Route path="platform/finance/force-refunds" element={<ForceRefundsPage />} />
              <Route path="platform/finance/deposit-reviews" element={<DepositReviewPage />} />
              <Route path="platform/finance/withdrawal-reviews" element={<WithdrawalReviewPage />} />
              <Route path="platform/finance/merchant-credits" element={<MerchantCreditReviewPage />} />
              <Route path="platform/finance/position-topups" element={<PositionTopupReviewPage />} />
              {/* ===== 账户钱包（2026-09-19-N4 一级组，路由不变）===== */}
              <Route path="platform/finance/merchant-wallet" element={<MerchantWalletPage />} />
              <Route path="platform/finance/va-accounts" element={<VaAccountsPage />} />
              <Route path="platform/finance/chain-accounts" element={<ChainAccountsPage />} />
              <Route path="platform/finance/treasury-positions" element={<TreasuryPositionsPage />} />

              {/* ===== 风控合规（预警/规则名单/KYC 复审/AML 已合并入壳；合规管理独立页保留）===== */}
              <Route path="platform/risk/alerts" element={<RiskAlertsShell />} />
              <Route path="platform/risk/rules" element={<RiskRulesShell />} />
              <Route path="platform/risk/kyc-review" element={<RiskKycReviewShell />} />
              <Route path="platform/risk/compliance" element={<CompliancePage />} />
              <Route path="platform/risk/aml" element={<RiskAmlShell />} />

              {/* ===== 运营中心（内容/客服/运维监控已合并入壳；AI 供应商独立页保留）===== */}
              <Route path="platform/ops/content" element={<OpsContentShell />} />
              <Route path="platform/ops/service" element={<OpsServiceShell />} />
              <Route path="platform/ops/ai-providers" element={<AiProvidersPage />} />
              <Route path="platform/ops/sre" element={<OpsSreShell />} />

              {/* ===== 报表中心（RPT-001~006 + BI 看板；三壳承接全部报表页）===== */}
              <Route path="platform/report/dashboard" element={<ReportDashboardShell />} />
              <Route path="platform/report/subscriptions" element={<ReportSubscriptionsShell />} />
              <Route path="platform/report/collab" element={<ReportCollabShell />} />

              {/* ===== 系统 ===== */}
              <Route path="platform/system/email-smtp" element={<EmailServicePage />} />
              <Route path="platform/system/security" element={<SecurityPage />} />
              <Route path="platform/system/settings" element={<SettingsPage />} />

              {/* ===== 旧路径集中重定向（扁平路由 + 历史挪位 + 被合并叶 → 壳?tab=x）===== */}
              {legacyRedirectRoutes}

              <Route path="*" element={<Navigate to="/platform" replace />} />
            </Route>
        </Route>
        </Route>
          </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineBanner />
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}
